/**
 * Payment Reassurance Modal - מודל אישור לפני מעבר ל-CardCom
 * Shown after the user picks a plan, just before redirecting to CardCom's
 * hosted payment page. CardCom's page is bare/unbranded, so users were unsure
 * it was legitimate — this step explains where they are being sent and that the
 * payment is secure, building confidence before the redirect.
 */

import { useRef } from 'react'
import { ShieldCheck, Lock, CreditCard, FileText, X } from 'lucide-react'
import { useDialogA11y } from '../../hooks/useDialogA11y'

export interface PaymentReassuranceModalProps {
  isOpen: boolean
  planLabel: string
  amountIls: number
  priceSuffix: string
  working: boolean
  onConfirm: () => void
  onClose: () => void
}

export default function PaymentReassuranceModal({
  isOpen,
  planLabel,
  amountIls,
  priceSuffix,
  working,
  onConfirm,
  onClose,
}: PaymentReassuranceModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  // While the redirect is in flight (working) the dialog must stay put.
  useDialogA11y(isOpen, dialogRef, working ? () => {} : onClose)

  if (!isOpen) return null

  return (
    // Backdrop click-to-dismiss is a mouse enhancement only; keyboard users
    // close via Escape (useDialogA11y) or the visible ביטול/סגירה buttons.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !working) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pay-reassure-title"
        aria-describedby="pay-reassure-desc"
        className="bg-white rounded-lg shadow-2xl max-w-md w-full border-t-4 border-green-500 animate-in fade-in zoom-in duration-200"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-3">
          <div className="flex items-center gap-3">
            <ShieldCheck size={40} className="text-green-600 flex-shrink-0" aria-hidden="true" />
            <h2 id="pay-reassure-title" className="text-xl font-bold text-gray-900">
              מועברים לעמוד תשלום מאובטח
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={working}
            aria-label="סגירה"
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Lead */}
        <div className="px-6">
          <p id="pay-reassure-desc" className="text-sm text-gray-600 leading-relaxed">
            מיד תועברו לעמוד התשלום של <span className="font-semibold text-gray-900">CardCom</span> —
            חברת סליקה מורשית בישראל המשמשת אלפי עסקים. עמוד התשלום נושא את המיתוג של CardCom, וזה תקין.
          </p>
        </div>

        {/* Trust rows */}
        <div className="px-6 pt-4 space-y-3">
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Lock size={18} className="text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-blue-900">
              פרטי האשראי מוצפנים (TLS) ואינם נשמרים אצלנו — התשלום מתבצע ישירות מול CardCom.
            </p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CreditCard size={18} className="text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-green-900">
              CardCom עומדת בתקן אבטחת התשלומים הבינלאומי PCI-DSS.
            </p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <FileText size={18} className="text-gray-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-gray-700">
              חשבונית מס/קבלה תופק אוטומטית לאחר התשלום.
            </p>
          </div>
        </div>

        {/* Plan + amount summary */}
        <div className="px-6 pt-4">
          <div className="flex items-baseline justify-between bg-primary-50/50 border border-primary-100 rounded-lg px-4 py-3">
            <span className="text-sm text-gray-600">{planLabel}</span>
            <span className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">{amountIls}</span>
              <span className="text-sm text-gray-600">{priceSuffix}</span>
            </span>
          </div>
        </div>

        {/* Extra info link */}
        <div className="px-6 pt-3">
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:text-primary-700 underline"
          >
            מידע נוסף על אבטחת התשלומים
          </a>
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-6 py-6 flex-col-reverse sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={working}
            className="btn btn-outline w-full sm:w-auto"
          >
            ביטול
          </button>
          <button
            onClick={onConfirm}
            disabled={working}
            className="btn btn-primary w-full sm:w-auto"
          >
            {working ? 'מעביר לעמוד התשלום...' : 'המשך לתשלום המאובטח'}
          </button>
        </div>
      </div>
    </div>
  )
}
